import { useNode } from '@craftjs/core';
import { Box, SxProps, Theme } from '@mui/material';

import {
  EducationalSlideH1,
  EducationalSlideH2,
  EducationalSlideH3,
  EducationalSlideH4,
} from '@/modules/presentation/clientPresentation/components/slides/EducationalSlide/components/EducationalSlide.components';

import { useIsBlockEditorEditMode } from '../../BlockEditor.context';
import { BLOCK_DISPLAY_NAMES } from '../../blockEditor.types';
import { EditableComponentWrapper } from '../../EditableComponentWrapper/EditableComponentWrapper';
import { EducationalSlideHeadingSettings } from './EducationalSlideHeadingSettings';

export type HeadingVariant = 'h1' | 'h2' | 'h3' | 'h4';

const HEADING_COMPONENTS: Record<HeadingVariant, typeof EducationalSlideH2> = {
  h1: EducationalSlideH1,
  h2: EducationalSlideH2,
  h3: EducationalSlideH3,
  h4: EducationalSlideH4,
};

export interface EducationalSlideHeadingProps {
  title: string;
  variant?: HeadingVariant;
  /**
   * Optional color override for the heading text.
   * This prop is not exposed in the settings form but can be set via the schema.
   */
  color?: string;
  /**
   * Optional sx prop for custom styling.
   * This prop is not exposed in the settings form but can be set via the schema.
   */
  sx?: SxProps<Theme>;
}

export function EducationalSlideHeading({
  title,
  variant = 'h2',
  color,
  sx,
}: EducationalSlideHeadingProps) {
  const {
    connectors: { connect, drag },
  } = useNode();
  const isEditMode = useIsBlockEditorEditMode();

  const HeadingComponent = HEADING_COMPONENTS[variant] ?? EducationalSlideH2;

  return (
    <EditableComponentWrapper>
      <Box
        ref={(ref: HTMLDivElement | null) => {
          if (ref && isEditMode) {
            connect(drag(ref));
          }
        }}
        sx={sx}
      >
        <HeadingComponent color={color}>{title}</HeadingComponent>
      </Box>
    </EditableComponentWrapper>
  );
}

EducationalSlideHeading.craft = {
  displayName: BLOCK_DISPLAY_NAMES.EducationalSlideHeading,
  props: {
    title: 'Section heading',
    variant: 'h2',
  },
  related: {
    settings: EducationalSlideHeadingSettings,
  },
};
